'use client'

import Image from 'next/image'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { RentToBuyDetailContent } from '@/lib/rent-to-buy-detail-content'
import { getRentToBuyOptions, getRentToBuySpecifications } from '@/lib/rent-to-buy-specs'

interface Props {
  vehicle: Record<string, unknown>
  content: RentToBuyDetailContent
}

const tabListClass = 'flex h-auto w-full gap-1 rounded-[14px] border border-[#dce4ee] bg-white p-1 text-[#4a586f]'
const tabClass = 'min-w-0 flex-1 whitespace-normal rounded-[10px] px-2 py-2.5 text-center text-xs font-bold leading-tight shadow-none data-[state=active]:bg-[#10223a] data-[state=active]:text-white data-[state=active]:shadow-none lg:py-[11px] lg:text-sm'
const panelClass = 'mt-3 rounded-[20px] border border-[#dce4ee] bg-white p-4 lg:p-6'

export function RentToBuyServices({ content }: Pick<Props, 'content'>) {
  return (
    <section id="details" aria-label={content.includedServicesLabel}>
      <h2 className="mb-3.5 text-[22px] font-extrabold text-[#10223a] lg:text-2xl">{content.detailsTitle}</h2>
      <Tabs defaultValue="included" className="w-full">
        <TabsList className={tabListClass} aria-label="Dettagli servizi Rent to Buy">
          <TabsTrigger value="included" className={tabClass}>{content.includedServicesLabel}</TabsTrigger>
          <TabsTrigger value="optional" className={tabClass}>{content.optionalServicesLabel}</TabsTrigger>
          <TabsTrigger value="how" className={tabClass}>{content.howItWorksLabel}</TabsTrigger>
        </TabsList>
        <TabsContent value="included" className={panelClass}>
          {content.includedServices.length > 0 ? <ul className="space-y-2.5">
            {content.includedServices.map((service, index) => (
              <li key={`${service}-${index}`} className="flex items-center gap-3 rounded-[14px] bg-[#e8f1fb] p-[14px] text-sm font-bold leading-[1.4] text-[#10223a] lg:text-[15px]">
                <Image src="/assets/rent-to-buy/icon-service-check.svg" width={20} height={20} alt="" aria-hidden="true" />
                <span>{service}</span>
              </li>
            ))}
          </ul> : <p className="text-sm text-[#4a586f]">{content.summaryUnavailable}</p>}
        </TabsContent>
        <TabsContent value="optional" className={panelClass}>
          <div className="space-y-5">
            {content.excludedServices.length > 0 && <div>
              <h3 className="mb-2.5 text-sm font-extrabold text-[#10223a] lg:text-[15px]">{content.excludedServicesTitle}</h3>
              <ul className="space-y-2.5">
                {content.excludedServices.map((service, index) => <li key={`${service}-${index}`} className="flex items-center gap-3 rounded-[14px] bg-[#fdecec] p-[14px] text-sm font-bold leading-[1.4] text-[#10223a] lg:text-[15px]">
                  <Image src="/assets/rent-to-buy/icon-service-excluded.svg" width={20} height={20} alt="" aria-hidden="true" />
                  <span>{service}</span>
                </li>)}
              </ul>
            </div>}
            {content.optionalServices.length > 0 && <div>
              <h3 className="mb-2.5 text-sm font-extrabold text-[#10223a] lg:text-[15px]">{content.paidServicesTitle}</h3>
              <ul className="space-y-2.5">
                {content.optionalServices.map((service, index) => <li key={`${service}-${index}`} className="flex items-center gap-3 rounded-[14px] bg-[#f3f6fa] p-[14px] text-sm font-bold leading-[1.4] text-[#10223a] lg:text-[15px]">
                  <Image src="/assets/rent-to-buy/icon-service-optional.svg" width={20} height={20} alt="" aria-hidden="true" />
                  <span>{service}</span>
                </li>)}
              </ul>
            </div>}
            {content.optionalServicesNotice && <p className="text-xs font-medium leading-[1.5] text-[#4a586f]">{content.optionalServicesNotice}</p>}
          </div>
        </TabsContent>
        <TabsContent value="how" className={panelClass}>
          <Accordion type="single" collapsible className="w-full">
            {content.howItWorksCategories.map((category, categoryIndex) => (
              <AccordionItem key={`${category.title}-${categoryIndex}`} value={`category-${categoryIndex}`} className="border-[#dce4ee] last:border-b-0">
                <AccordionTrigger className="px-1 py-4 text-left text-[15px] font-bold text-[#10223a] hover:no-underline lg:text-base">
                  {category.title}
                </AccordionTrigger>
                <AccordionContent className="pb-0">
                  <Accordion type="single" collapsible className="space-y-2 pb-4">
                    {category.questions.map((question, questionIndex) => (
                      <AccordionItem key={`${question.title}-${questionIndex}`} value={`question-${questionIndex}`} className="rounded-xl border-0 bg-[#f3f6fa] px-4">
                        <AccordionTrigger className="py-3.5 text-left text-sm font-semibold leading-[1.4] text-[#10223a] hover:no-underline">
                          {question.title}
                        </AccordionTrigger>
                        <AccordionContent className="space-y-1.5 pb-3.5 text-sm leading-[1.55] text-[#4a586f]">
                          {question.body.split(/\n\s*\n/).map((paragraph, paragraphIndex) => (
                            <p key={paragraphIndex}>{paragraph}</p>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </TabsContent>
      </Tabs>
    </section>
  )
}

export function RentToBuySpecifications({ vehicle, content }: Props) {
  const specifications = getRentToBuySpecifications(vehicle, content.summaryUnavailable)
  const options = getRentToBuyOptions(vehicle.accessori)

  return (
    <section id="specifications" aria-label={content.technicalTabLabel}>
      <h2 className="mb-3 text-[22px] font-extrabold text-[#10223a] lg:mb-3.5 lg:text-2xl">{content.specificationsTitle}</h2>
      <Tabs defaultValue="technical" className="w-full">
        <TabsList className={tabListClass} aria-label="Caratteristiche del veicolo">
          <TabsTrigger value="technical" className={tabClass}>{content.technicalTabLabel}</TabsTrigger>
          <TabsTrigger value="options" className={tabClass}>{content.vehicleOptionsLabel}</TabsTrigger>
        </TabsList>
        <TabsContent value="technical" className={panelClass}>
          <dl className="grid grid-cols-2 gap-x-3 lg:grid-cols-4 lg:gap-x-6">
            {specifications.map(({ label, value }) => (
              <div key={label} className="min-w-0 border-b border-[#dce4ee] py-3">
                <dt className="text-xs font-semibold text-[#4a586f]">{label}</dt>
                <dd className="mt-1 break-words text-sm font-extrabold leading-snug text-[#10223a] lg:text-[15px]">{value}</dd>
              </div>
            ))}
          </dl>
        </TabsContent>
        <TabsContent value="options" className={panelClass}>
          {options.length > 0 ? (
            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
              {options.map((option, index) => (
                <li key={`${option}-${index}`} className="min-w-0 break-words rounded-[10px] bg-[#e8f1fb] px-3 py-2.5 text-xs font-semibold leading-[1.3] text-[#10223a]">{option}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[#4a586f]">{content.summaryUnavailable}</p>
          )}
        </TabsContent>
      </Tabs>
    </section>
  )
}
